/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Combobox,
    DataGridCell,
    DataGridHeaderCell,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Field,
    Input,
    makeStyles,
    MessageBar,
    MessageBarBody,
    Option,
    Spinner,
    tokens
} from '@fluentui/react-components';
import { Add24Regular, DeleteRegular, Dismiss24Regular, DocumentAdd24Regular, Edit24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { PersonalItem } from '../../models/PersonalItem';
import type { StundensatzItem } from '../../models/StundensatzItem';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';

const EXCEL_COLUMNS: ExcelExportColumn<StundensatzItem>[] = [
    { header: 'Year', value: (item) => item.jahr, width: 10 },
    { header: 'Month', value: (item) => item.monat, width: 10 },
    { header: 'Employee', value: (item) => item.personalName, width: 35 },
    { header: 'Hourly Rate (€)', value: (item) => item.stundensatz, width: 18 },
];

const ROW_HEIGHT = 40; // px

// Column weights for proportional widths
const COLUMN_WEIGHTS: Record<string, number> = {
    action: 0.4,
    jahr: 0.8,
    monat: 0.8,
    employee: 2,
    stundensatz: 1.2,
};

const COLUMN_ORDER = ['action', 'jahr', 'monat', 'employee', 'stundensatz'] as const;

type SortColumn = keyof StundensatzItem | null;
type SortConfig = { column: SortColumn; direction: 'asc' | 'desc' };

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: tokens.spacingHorizontalL,
    },
    buttonGroup: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
    },
    filterContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        flex: 1,
    },
    searchInput: {
        flex: 1,
        maxWidth: '400px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    dataGridContainer: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    actionButtons: {
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
    },
    // Drawer styles
    section: {
        marginBottom: tokens.spacingVerticalL,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    formGroup: {
        display: 'grid',
        gap: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalL,
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
    footerActions: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'flex-end',
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    fileInputContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: tokens.spacingVerticalM,
        border: `1px dashed ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    fileInputLabel: {
        cursor: 'pointer',
        flex: 1,
    },
    hiddenFileInput: {
        display: 'none',
    },
});

// Calculate proportional column widths
const calculateColumnWidth = (columnId: string): number => {
    const totalWeight = Object.values(COLUMN_WEIGHTS).reduce((a, b) => a + b, 0);
    return (COLUMN_WEIGHTS[columnId] / totalWeight) * 100;
};

interface DrawerState {
    isOpen: boolean;
    item: StundensatzItem | null;
    isEditMode: boolean;
}

interface ImportDrawerState {
    isOpen: boolean;
    file: File | null;
    dateVon: string;
    dateBis: string;
    isImporting: boolean;
}

const HourlyRatesTab: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [items, setItems] = useState<StundensatzItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [sortConfigs, setSortConfigs] = useState<SortConfig[]>([
        { column: 'jahr', direction: 'desc' },
        { column: 'monat', direction: 'desc' },
    ]);
    const [drawerState, setDrawerState] = useState<DrawerState>({
        isOpen: false,
        item: null,
        isEditMode: false,
    });
    const [formData, setFormData] = useState<StundensatzItem>({
        id: 0,
        jahr: new Date().getFullYear(),
        monat: new Date().getMonth() + 1,
        stundensatz: 0,
        personalID: 0,
        personalName: '',
    } as Partial<StundensatzItem> as StundensatzItem);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<StundensatzItem | null>(null);
    const [importDrawerState, setImportDrawerState] = useState<ImportDrawerState>({
        isOpen: false,
        file: null,
        dateVon: '',
        dateBis: '',
        isImporting: false,
    });
    const [displayedItemsCount, setDisplayedItemsCount] = useState(200); // Pagination: show 200 items at a time
    const [mitarbeiter, setMitarbeiter] = useState<PersonalItem[]>([]);
    const [mitarbeiterSearchResults, setMitarbeiterSearchResults] = useState<PersonalItem[]>([]);
    const [employeeSearchInput, setEmployeeSearchInput] = useState('');

    const [messageBarState, setMessageBarState] = useState<{
        visible: boolean;
        message: string;
        intent: 'success' | 'error';
    }>({ visible: false, message: '', intent: 'success' });
    const [importErrorMessage, setImportErrorMessage] = useState<string>('');

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch all items
    useEffect(() => {
        const fetchItems = async () => {
            if (!dataService) {
                return;
            }

            try {
                setIsLoading(true);
                const data = await dataService.getProjektStundensatzItems();
                console.log('Fetched Stundensatz items:', data);
                setItems(data || []);
            } catch (err) {
                console.error('Error loading Stundensatz items:', err);
                setItems([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchItems();
    }, [dataService]);

    // Reset pagination when search or sort changes
    useEffect(() => {
        setDisplayedItemsCount(200);
    }, [filterText, sortConfigs]);

    // Fetch mitarbeiter (employees) for combobox
    useEffect(() => {
        const fetchMitarbeiter = async () => {
            if (!dataService) {
                return;
            }

            try {
                const data = await dataService.getMitarbeiter();
                setMitarbeiter(data || []);
                setMitarbeiterSearchResults(data || []);
            } catch (err) {
                console.error('Error loading Mitarbeiter:', err);
                setMitarbeiter([]);
                setMitarbeiterSearchResults([]);
            }
        };

        fetchMitarbeiter();
    }, [dataService]);

    // Filter items based on search text
    const filteredItems = useMemo(() => {
        let results = items;

        // Apply search filter
        if (filterText.trim()) {
            const searchLower = filterText.toLowerCase();
            results = results.filter((item) => {
                return (
                    item.personalName?.toLowerCase().includes(searchLower) ||
                    item.jahr?.toString().includes(searchLower) ||
                    item.monat?.toString().includes(searchLower) ||
                    item.stundensatz?.toString().includes(searchLower)
                );
            });
        }

        // Apply sorting (multi-level)
        if (sortConfigs.length > 0) {
            results = [...results].sort((a, b) => {
                // Apply each sort level in order
                for (const { column, direction } of sortConfigs) {
                    if (!column) continue;

                    let aValue: any = a[column];
                    let bValue: any = b[column];

                    // Handle null/undefined
                    if (aValue == null) aValue = '';
                    if (bValue == null) bValue = '';

                    // Convert to lowercase for string comparison
                    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

                    // Comparison
                    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
                    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
                    // Continue to next sort level if equal
                }
                return 0;
            });
        }

        return results;
    }, [items, filterText, sortConfigs]);

    const handleSort = useCallback((column: SortColumn) => {
        // Find if this column is already in sort configs
        const existingIndex = sortConfigs.findIndex((cfg) => cfg.column === column);

        if (existingIndex === 0) {
            // Primary sort - toggle direction
            const newConfigs = [...sortConfigs];
            newConfigs[0].direction = newConfigs[0].direction === 'asc' ? 'desc' : 'asc';
            setSortConfigs(newConfigs);
        } else if (existingIndex > 0) {
            // Already in secondary position - move to primary with toggled direction
            const newConfigs = [...sortConfigs];
            const config = newConfigs.splice(existingIndex, 1)[0];
            config.direction = config.direction === 'asc' ? 'desc' : 'asc';
            newConfigs.unshift(config);
            setSortConfigs(newConfigs);
        } else {
            // New column - add as primary, push others down
            setSortConfigs([
                { column, direction: 'desc' },
                ...sortConfigs.slice(0, 1), // Keep secondary sort
            ]);
        }
    }, [sortConfigs]);

    const handleMitarbeiterSearch = useCallback((searchValue: string) => {
        setEmployeeSearchInput(searchValue);

        if (searchValue.length < 2) {
            setMitarbeiterSearchResults([]);
            return;
        }

        const searchLower = searchValue.toLowerCase();
        const filtered = mitarbeiter.filter((m) => {
            const fullName = m.fullName || `${m.vorname} ${m.nachname}`;
            return (
                fullName.toLowerCase().includes(searchLower) ||
                m.vorname?.toLowerCase().includes(searchLower) ||
                m.nachname?.toLowerCase().includes(searchLower)
            );
        });

        setMitarbeiterSearchResults(filtered);
    }, [mitarbeiter]);

    const handleAddNew = () => {
        setFormData({
            id: 0,
            jahr: new Date().getFullYear(),
            monat: new Date().getMonth() + 1,
            stundensatz: 0,
            personalID: 0,
            personalName: '',
        } as Partial<StundensatzItem> as StundensatzItem);
        setEmployeeSearchInput('');
        setMitarbeiterSearchResults([]);
        setDrawerState({
            isOpen: true,
            item: null,
            isEditMode: false,
        });
    };

    const handleView = (item: StundensatzItem) => {
        setFormData(item);
        setEmployeeSearchInput(item.personalName || '');
        setMitarbeiterSearchResults([]);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: false,
        });
    };

    const handleEdit = (item: StundensatzItem) => {
        setFormData(item);
        setEmployeeSearchInput(item.personalName || '');
        setMitarbeiterSearchResults([]);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: true,
        });
    };

    const handleRowDoubleClick = (item: StundensatzItem) => {
        handleView(item);
    };

    const handleDrawerClose = () => {
        setDrawerState({
            isOpen: false,
            item: null,
            isEditMode: false,
        });
        setFormData({
            id: 0,
            jahr: new Date().getFullYear(),
            monat: new Date().getMonth() + 1,
            stundensatz: 0,
            personalID: 0,
            personalName: '',
        } as Partial<StundensatzItem> as StundensatzItem);
        setEmployeeSearchInput('');
        setMitarbeiterSearchResults([]);
    };

    const handleDrawerSave = async () => {
        if (!dataService) return;

        if (!formData.personalName?.trim() || formData.stundensatz <= 0) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setIsSaving(true);

            if (drawerState.isEditMode && drawerState.item) {
                // Update existing
                await dataService.updateStundensatzItem(formData);
                setItems((prevItems) =>
                    prevItems.map((p) => (p.id === formData.id ? formData : p))
                );
            } else {
                // Add new
                await dataService.addStundensatzItem(formData);
                // Reload the list
                const data = await dataService.getProjektStundensatzItems();
                setItems(data || []);
            }

            handleDrawerClose();
        } catch (err) {
            console.error('Error saving Stundensatz:', err);
            alert('Error saving Stundensatz: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (item: StundensatzItem) => {
        setItemToDelete(item);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!dataService || !itemToDelete) return;

        try {
            setIsSaving(true);
            await dataService.deleteStundensatzItem(itemToDelete.id);

            // Remove the item from the list
            setItems((prevItems) =>
                prevItems.filter((p) => p.id !== itemToDelete.id)
            );

            setShowDeleteDialog(false);
            setItemToDelete(null);
        } catch (err) {
            console.error('Error deleting Stundensatz:', err);
            alert('Error deleting Stundensatz: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleImportClick = () => {
        setImportDrawerState({
            isOpen: true,
            file: null,
            dateVon: '',
            dateBis: '',
            isImporting: false,
        });
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setImportDrawerState((prev) => ({
            ...prev,
            file: file,
        }));
    };

    const handleImportDrawerClose = () => {
        setImportDrawerState({
            isOpen: false,
            file: null,
            dateVon: '',
            dateBis: '',
            isImporting: false,
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const isImportValid = () => {
        return (
            importDrawerState.file !== null &&
            importDrawerState.dateVon.trim() !== '' &&
            importDrawerState.dateBis.trim() !== ''
        );
    };

    const handleConfirmImport = async () => {
        if (!dataService || !importDrawerState.file || !isImportValid()) return;

        try {
            setImportDrawerState((prev) => ({ ...prev, isImporting: true }));
            setImportErrorMessage('');

            const dateVon = new Date(importDrawerState.dateVon);
            const dateBis = new Date(importDrawerState.dateBis);

            const result = await dataService.importStundensatzItems(
                importDrawerState.file,
                dateVon,
                dateBis
            );

            // Build success message
            let successMessage = `Successfully imported ${result.rowsProcessed} rows`;
            if (result.errors && result.errors.length > 0) {
                successMessage += ` with ${result.errors.length} error(s)`;
            }

            // Show success message and close drawer
            setMessageBarState({
                visible: true,
                message: successMessage,
                intent: 'success',
            });

            // Auto-hide message after 5 seconds
            setTimeout(() => {
                setMessageBarState({ visible: false, message: '', intent: 'success' });
            }, 5000);

            // Reload the list
            const data = await dataService.getProjektStundensatzItems();
            setItems(data || []);

            // Close drawer
            handleImportDrawerClose();
        } catch (err) {
            console.error('Error importing Stundensatz:', err);
            const errorMsg = err instanceof Error ? err.message : 'Unknown error';

            // Show error in drawer and keep it open
            setImportErrorMessage(errorMsg);
            setMessageBarState({
                visible: false,
                message: '',
                intent: 'error',
            });
        } finally {
            setImportDrawerState((prev) => ({ ...prev, isImporting: false }));
        }
    };

    const isFormValid = useCallback(() => {
        if (!formData.personalName || formData.personalName.trim() === '') {
            return false;
        }

        if (formData.stundensatz <= 0) {
            return false;
        }

        if (formData.jahr <= 0 || formData.monat <= 0 || formData.monat > 12) {
            return false;
        }

        return true;
    }, [formData]);

    return (
        <div className={styles.container}>
            {/* Header with Filter and Add Buttons */}
            <div className={styles.headerContainer}>
                <div className={styles.filterContainer}>
                    <Input
                        placeholder="Search by employee, year or rate..."
                        value={filterText}
                        onChange={(_, data) => setFilterText(data.value)}
                        className={styles.searchInput}
                    />
                    <ExcelExportButton
                        items={filteredItems}
                        columns={EXCEL_COLUMNS}
                        fileName={`HourlyRates_${new Date().toISOString().split('T')[0]}`}
                        sheetName="Hourly Rates"
                    />
                </div>
                <div className={styles.buttonGroup}>
                    <Button
                        icon={<DocumentAdd24Regular />}
                        appearance="secondary"
                        onClick={handleImportClick}
                    >
                        Import Stundensätze
                    </Button>
                    <Button
                        icon={<Add24Regular />}
                        appearance="primary"
                        onClick={handleAddNew}
                    >
                        Add Hourly Rate
                    </Button>
                </div>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading hourly rates..." />
                </div>
            )}

            {/* Empty State */}
            {!isLoading && items.length === 0 && (
                <div className={styles.emptyState}>
                    No hourly rates found. Click "Add Hourly Rate" or "Import Stundensätze" to create entries.
                </div>
            )}

            {/* Success Message Bar */}
            {messageBarState.visible && messageBarState.intent === 'success' && (
                <MessageBar intent="success">
                    <MessageBarBody>{messageBarState.message}</MessageBarBody>
                </MessageBar>
            )}

            {/* Items DataGrid */}
            {!isLoading && filteredItems.length > 0 && (
                <div className={styles.dataGridContainer}>
                    {/* Header Row */}
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            backgroundColor: tokens.colorNeutralBackground3,
                            borderBottom: '1px solid #d1d5db',
                            fontWeight: 600,
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                        }}
                    >
                        {COLUMN_ORDER.map((columnId) => {
                            const width = calculateColumnWidth(columnId);
                            const isSortable = columnId !== 'action';
                            return (
                                <DataGridHeaderCell
                                    key={columnId}
                                    style={{
                                        flex: `0 0 ${width}%`,
                                        paddingLeft: '8px',
                                        paddingRight: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRight: '1px solid #e1e1e1',
                                        boxSizing: 'border-box',
                                        overflow: 'hidden',
                                        cursor: isSortable ? 'pointer' : 'default',
                                    }}
                                    onClick={() => {
                                        if (isSortable) {
                                            handleSort(columnId === 'employee' ? 'personalName' : columnId as SortColumn);
                                        }
                                    }}
                                >
                                    {(() => {
                                        const sortIndex = sortConfigs.findIndex((cfg) => cfg.column === (columnId === 'employee' ? 'personalName' : columnId));
                                        const sortIndicator = sortIndex >= 0
                                            ? ` ${sortConfigs[sortIndex].direction === 'asc' ? '▲' : '▼'}${sortIndex > 0 ? ` (${sortIndex + 1})` : ''}`
                                            : '';

                                        if (columnId === 'action') return 'Actions';
                                        if (columnId === 'jahr') return `Year${sortIndicator}`;
                                        if (columnId === 'monat') return `Month${sortIndicator}`;
                                        if (columnId === 'employee') return `Employee${sortIndicator}`;
                                        if (columnId === 'stundensatz') return `Hourly Rate${sortIndicator}`;
                                    })()}
                                </DataGridHeaderCell>
                            );
                        })}
                    </div>

                    {/* Data Rows */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                    >
                        {filteredItems.slice(0, displayedItemsCount).map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    display: 'flex',
                                    height: ROW_HEIGHT,
                                    borderBottom: '1px solid #e1e1e1',
                                    cursor: 'pointer',
                                }}
                                onDoubleClick={() => handleRowDoubleClick(item)}
                            >
                                {COLUMN_ORDER.map((columnId) => {
                                    const width = calculateColumnWidth(columnId);
                                    return (
                                        <DataGridCell
                                            key={columnId}
                                            style={{
                                                flex: `0 0 ${width}%`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                paddingLeft: '8px',
                                                paddingRight: '8px',
                                                borderRight: '1px solid #e1e1e1',
                                                boxSizing: 'border-box',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            role="gridcell"
                                        >
                                            {columnId === 'action' && (
                                                <div className={styles.actionButtons}>
                                                    <Button
                                                        icon={<Edit24Regular />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEdit(item);
                                                        }}
                                                        appearance="subtle"
                                                        size="small"
                                                        title="Edit"
                                                    />
                                                    <Button
                                                        icon={<DeleteRegular />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(item);
                                                        }}
                                                        appearance="subtle"
                                                        size="small"
                                                        title="Delete"
                                                    />
                                                </div>
                                            )}
                                            {columnId === 'jahr' && (item.jahr || '-')}
                                            {columnId === 'monat' && (item.monat || '-')}
                                            {columnId === 'employee' && (item.personalName || '-')}
                                            {columnId === 'stundensatz' && `€ ${item.stundensatz?.toFixed(2) || '-'}`}
                                        </DataGridCell>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* Load More Button */}
                    {displayedItemsCount < filteredItems.length && (
                        <div
                            style={{
                                padding: '10px',
                                textAlign: 'center',
                                borderTop: '1px solid #e1e1e1',
                                backgroundColor: '#fafafa',
                            }}
                        >
                            <Button
                                onClick={() => setDisplayedItemsCount((prev) => prev + 200)}
                                appearance="secondary"
                            >
                                Load More ({filteredItems.length - displayedItemsCount} remaining)
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {/* No results after filtering */}
            {!isLoading && items.length > 0 && filteredItems.length === 0 && (
                <div className={styles.emptyState}>
                    No hourly rates match your search
                </div>
            )}

            {/* Drawer for Add/Edit */}
            <Drawer
                open={drawerState.isOpen}
                onOpenChange={(_, { open }) => {
                    if (!open) handleDrawerClose();
                }}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={handleDrawerClose}
                            />
                        }
                    >
                        {drawerState.isEditMode ? 'Edit Hourly Rate' : drawerState.item ? 'View Hourly Rate' : 'Add Hourly Rate'}
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody>
                    <div className={styles.section}>
                        <div className={styles.formGroup}>
                            <div className={styles.fieldRow}>
                                <Field label="Year" required>
                                    <Input
                                        type="number"
                                        value={formData.jahr?.toString() || ''}
                                        onChange={(_, data) =>
                                            setFormData({ ...formData, jahr: parseInt(data.value, 10) || 0 })
                                        }
                                        disabled={!drawerState.isEditMode && drawerState.item !== null}
                                        placeholder="2024"
                                    />
                                </Field>
                                <Field label="Month" required>
                                    <Input
                                        type="number"
                                        value={formData.monat?.toString() || ''}
                                        onChange={(_, data) => {
                                            const val = parseInt(data.value, 10) || 0;
                                            if (val >= 1 && val <= 12) {
                                                setFormData({ ...formData, monat: val });
                                            }
                                        }}
                                        disabled={!drawerState.isEditMode && drawerState.item !== null}
                                        placeholder="1-12"
                                    />
                                </Field>
                            </div>
                            <Field label="Employee" required>
                                <Combobox
                                    placeholder="Enter at least 2 characters..."
                                    value={employeeSearchInput}
                                    onOptionSelect={(_, option) => {
                                        const member = mitarbeiterSearchResults.find(
                                            (m) => m.personalID === parseInt(option.optionValue || '0')
                                        );
                                        if (member) {
                                            const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                            setFormData({
                                                ...formData,
                                                personalName: fullName,
                                                personalID: member.personalID,
                                            });
                                            setEmployeeSearchInput(fullName);
                                            setMitarbeiterSearchResults([]);
                                        }
                                    }}
                                    onChange={(ev) => {
                                        const searchValue = (ev.target as HTMLInputElement).value;
                                        handleMitarbeiterSearch(searchValue);
                                    }}
                                    appearance="outline"
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                >
                                    {mitarbeiterSearchResults.map((member) => {
                                        const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                        return (
                                            <Option
                                                key={member.personalID}
                                                value={member.personalID.toString()}
                                                text={fullName}
                                            >
                                                {fullName}
                                            </Option>
                                        );
                                    })}
                                </Combobox>
                            </Field>
                            <Field label="Hourly Rate (€)" required>
                                <Input
                                    type="number"
                                    value={formData.stundensatz?.toString() || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, stundensatz: parseFloat(data.value) || 0 })
                                    }
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="0.00"
                                    step="0.01"
                                />
                            </Field>
                        </div>

                        {/* Footer Actions */}
                        <div className={styles.footerActions}>
                            <Button
                                appearance="secondary"
                                onClick={handleDrawerClose}
                                disabled={isSaving}
                            >
                                {drawerState.isEditMode || !drawerState.item ? 'Cancel' : 'Close'}
                            </Button>
                            {(drawerState.isEditMode || !drawerState.item) && (
                                <Button
                                    appearance="primary"
                                    onClick={handleDrawerSave}
                                    disabled={isSaving || !isFormValid()}
                                >
                                    {isSaving ? <Spinner size="tiny" /> : 'Save'}
                                </Button>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </Drawer>

            {/* Import Drawer */}
            <Drawer
                open={importDrawerState.isOpen}
                onOpenChange={(_, { open }) => {
                    if (!open) handleImportDrawerClose();
                }}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={handleImportDrawerClose}
                            />
                        }
                    >
                        Import Stundensätze
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody>
                    <div className={styles.section}>
                        <div className={styles.formGroup}>
                            <Field label="Date From" required>
                                <Input
                                    type="date"
                                    value={importDrawerState.dateVon}
                                    onChange={(_, data) =>
                                        setImportDrawerState((prev) => ({ ...prev, dateVon: data.value }))
                                    }
                                    placeholder="Select start date"
                                />
                            </Field>
                            <Field label="Date To" required>
                                <Input
                                    type="date"
                                    value={importDrawerState.dateBis}
                                    onChange={(_, data) =>
                                        setImportDrawerState((prev) => ({ ...prev, dateBis: data.value }))
                                    }
                                    placeholder="Select end date"
                                />
                            </Field>
                            <Field label="Excel File (.xls, .xlsx)" required>
                                <div
                                    className={styles.fileInputContainer}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <DocumentAdd24Regular />
                                    <div className={styles.fileInputLabel}>
                                        {importDrawerState.file ? (
                                            <span>{importDrawerState.file.name}</span>
                                        ) : (
                                            <span>Click to select file or drag and drop</span>
                                        )}
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xls,.xlsx,.csv,.txt"
                                    onChange={handleFileSelect}
                                    className={styles.hiddenFileInput}
                                />
                            </Field>
                        </div>

                        {/* Footer Actions */}
                        <div className={styles.footerActions}>
                            <Button
                                appearance="secondary"
                                onClick={handleImportDrawerClose}
                                disabled={importDrawerState.isImporting}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleConfirmImport}
                                disabled={importDrawerState.isImporting || !isImportValid()}
                            >
                                {importDrawerState.isImporting ? <Spinner size="tiny" /> : 'Import Hourly Rates'}
                            </Button>
                        </div>

                        {importErrorMessage && (
                            <div
                                style={{
                                    backgroundColor: '#fed7d7',
                                    border: '1px solid #fc8181',
                                    borderRadius: '4px',
                                    padding: '12px',
                                    marginBottom: '16px',
                                    color: '#c53030',
                                }}
                            >
                                <strong>Error:</strong> {importErrorMessage}
                            </div>
                        )}
                    </div>
                </DrawerBody>
            </Drawer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={(_, data) => setShowDeleteDialog(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>
                            Delete Hourly Rate
                        </DialogTitle>
                        <DialogContent>
                            Are you sure you want to delete the hourly rate for{' '}
                            <strong>{itemToDelete?.personalName}</strong> ({itemToDelete?.jahr}/{itemToDelete?.monat})?
                        </DialogContent>
                        <div
                            style={{
                                display: 'flex',
                                gap: tokens.spacingHorizontalM,
                                justifyContent: 'flex-end',
                                paddingTop: tokens.spacingVerticalL,
                            }}
                        >
                            <Button
                                appearance="secondary"
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleConfirmDelete}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner size="tiny" /> : 'Delete'}
                            </Button>
                        </div>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default HourlyRatesTab;
